import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {Translations} from './translations.js';
import template from './ns-shipping-card.html';
import {ShippingService} from '@nuskin/ns-shop';
import {TranslationsUtil} from '@nuskin/ns-util';

/**
 * Image Imports
 */
import deleteIcon from '../img/icon-trash.png';
import closeIcon from '../img/ic-close-small.svg';
import warningIcon from '../img/warning-icon.svg';

/**
 * This component is used for the Shipping Address List as a part ADR Manage Shipping Address.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Shipping Card Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-shipping-card is-demo></ns-shipping-card>
 *   </template>
 * </demo-snippet>
 */
export class NsShippingCard extends PolymerElement {
    static get is() {
        return 'ns-shipping-card';
    }

    /**
     * Sets the html template
     */
    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    /**
     * Properties to pass to this component
     */
    static get properties() {
        return {
            /**
             * Currently selected ADR object.
             */
            selectedAdr: {
                type: Object,
                notify: true
            },
            /**
             * Current language selected
             */
            language: {
                type: String
            },
            /**
             * Current country selected
             */
            countryCode: {
                type: String
            },
            /**
             * Encoded value of close icon used for passing this value to the template
             */
            closeIcon: {
                type: String,
                value: closeIcon
            },
            /**
             * Encoded value of delete icon used for passing this value to the template
             */
            deleteIcon: {
                type: String,
                value: deleteIcon
            },
            /**
             * Encoded value of warning icon used for passing this value to the template
             */
            warningIcon: {
                type: String,
                value: warningIcon
            },
            pageName: {
                type: String
            },
            /**
             * Allows disabling all delete buttons, for testing purposes.
             */
            enableDelete: {
                type: Boolean,
                value: true
            },
            /**
             * Populates component with test data and prevents any unnecessary network calls.
             */
            isDemo: {
                type: Boolean,
                value: false
            },
            /**
             * Page upon which this component appears. Used for translations.
             */
            webPageName: {
                type: String,
                value: 'shop/adp-manage'
            },
            /**
             * Translations from the server. Triggers the translation assignment process.
             * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
             */
            serverTranslations: {
                type: Object,
                observer: '_assignTranslations'
            },
            /**
             * If false, it will start the translation assignment process without waiting.
             * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
             * If serverTranslations is defined already, the component will behave as if this property is true.
             */
            shouldAwaitTranslations: {
                type: Boolean,
                value: false
            },
            /**
             * Array of pick up options
             */
            pickUpOptions: {
                type: Array,
                notify: true
            }
        };
    }

    /**
     * Sets up the parent component
     */
    ready() {
        super.ready();

        if (!this.TRANSLATION_HELPER) this._localize();

        if (this.isDemo) {
            this._setDemoAddress();
            this.shippingService = ShippingService;
        }
    }

    /**
     * Sets language and country properties and prepares translations
     */
    _localize() {
        this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
        this.language = this.TRANSLATION_HELPER.language;
        this.country = this.TRANSLATION_HELPER.country;
        if (!this.shouldAwaitTranslations) this._assignTranslations();
    }

    /**
     * Gathers all translations and maps them to properties.
     * Any strings set as html attributes take precedence over translations from the server,
     * which in turn take precedence over the default translations in the translations file.
     */
    async _assignTranslations() {
        if (!this.TRANSLATION_HELPER) this._localize();
        this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
        let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
        Object.entries(allTranslations).forEach(([key, value]) => {
            this[key] = this[key] || value;
        });
    }

    /**
     * Toggles the delete confirmation modal
     */
    _toggleDeleteAddressModal() {
        const deleteModal = this.$.deleteAddressModal;
        deleteModal.toggle();
        if (this.pageName && this.pageName !== 'Profile') deleteModal.modal = true;
    }

    /**
     * Submits delete address
     */
    _deleteAddress() {
        ShippingService.deleteShippingAddress(this.beShippingAddressId).then((result) => {
            this.deletedAddress = result.ShippingAddress;
            this.ship.showShippingAddressList();
        });
        this._toggleDeleteAddressModal();
    }

    /**
     * Sets up address
     */
    _setAddress() {
        let address = {
            shippingIndex: this.shippingIndex,
            beShippingAddressId: this.beShippingAddressId,
            shippingAlias: this.shippingAlias,
            shippingAddressName: this.shippingAddressName,
            shippingAddress1: this.shippingAddress1,
            shippingAddress2: this.shippingAddress2,
            shippingCity: this.shippingCity,
            shippingState: this.shippingState,
            shippingPostalCode: this.shippingPostalCode,
            shippingCountry: this.shippingCountry,
            shippingPhone: this.shippingPhone,
            shippingPhoneCountry: this.shippingPhoneCountry,
            shippingMobilePhone: this.shippingMobilePhone,
            shippingMobilePhoneCountry: this.shippingMobilePhoneCountry,
            shippingEmail: this.shippingEmail,
            shippingCounty: this.shippingCounty,
            shippingInstructions: this.shippingInstructions,
            isDefaultShipping: this.isDefaultShipping,
            isSavedAddress: this.isSavedAddress
        };
        return address;
    }

    _setDemoAddress() {
        this.index = '1';
        this.shippingAlias = 'Alias';
        this.shippingAddressName = 'John Smith';
        this.shippingAddress1 = '123 Grove Ln';
        this.shippingCity = 'Washington';
        this.shippingState = 'DC';
        this.shippingPostalCode = '12231';
        this.shippingCountry = 'US';
        this.shippingPhone = '231-345-1237';
        this.shippingEmail = 'jsmith@email.com';
    }

    /**
     * Opens up Shipping Address Form to edit address
     */
    _updateAddress() {
        if (this.isDemo) {
            return;
        }
        // Reset pickUpOptions to undefined when user updates their address to
        // get new pickUpOptions based on new address.
        this.pickUpOptions = undefined;
        this.ship.showShippingAddressForm(this._setAddress());
    }

    /**
     * Sets shipping address for ADR selected
     */
    _setShippingAddress() {
        if (this.isDemo) {
            return;
        }
        // Reset pickUpOptions to undefined when user selects a new address to
        // get new pickUpOptions based on new address.
        this.pickUpOptions = undefined; 
        this.ship._setShippingAddressResponse(this._setAddress());
    }

    /**
     * Sets the view to switch between different views
     * @param {String} message
     */
    _isView(prop, message) {
        return prop === message;
    }
}

customElements.define(NsShippingCard.is, NsShippingCard);
